var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import '@/styles/Rooms/RoomsPage.less';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useApiInstance } from '@/api';
import { Icon } from '@/assets/icons/LibrarySvg';
import Loader from '@/components/UI/Loader';
import NotificationPopup from '@/components/UI/NotificationPopup';
import { useApiContext } from '@/context/ApiContext';
import { useAuth } from '@/context/AuthContext';
import { useNotification } from '@/hooks/useNotification';
const DEFAULT_TOOLTIP = { roomId: null, type: null };
const RoomsPage = () => {
    const [roomCode, setRoomCode] = useState('');
    const [createRoomName, setCreateRoomName] = useState('');
    const [loading, setLoading] = useState(false);
    const [userRooms, setUserRooms] = useState([]);
    const [roomsLoading, setRoomsLoading] = useState(false);
    const [tooltip, setTooltip] = useState(Object.assign({}, DEFAULT_TOOLTIP));
    const [editingRoomId, setEditingRoomId] = useState(null);
    const [editRoomName, setEditRoomName] = useState('');
    const { useMockApi } = useApiContext();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { t } = useTranslation();
    const api = useApiInstance();
    const roomApi = api.roomApiInstance;
    const editInputRef = useRef(null);
    // notifications
    const { notificationList, showNotification, showError } = useNotification(2000);
    // Подключение к комнате
    const handleJoin = (e) => __awaiter(void 0, void 0, void 0, function* () {
        e.preventDefault();
        if (!roomCode.trim()) {
            showError(t('rooms.enterRoomCode'));
            return;
        }
        setLoading(true);
        try {
            const room = yield roomApi.getRoomByCode(roomCode.trim());
            if (room) {
                navigate(`/room/${room.code}`);
            }
            else {
                showError(t('rooms.roomNotFound'));
            }
        }
        catch (err) {
            showError(t('rooms.roomNotFound'));
        }
        finally {
            setLoading(false);
        }
    });
    // Создание комнаты
    const handleCreate = (e) => __awaiter(void 0, void 0, void 0, function* () {
        e.preventDefault();
        if (!createRoomName.trim()) {
            showError(t('rooms.enterRoomName'));
            return;
        }
        setLoading(true);
        try {
            const room = yield roomApi.create(createRoomName.trim());
            if (room && room.code) {
                navigate(`/room/${room.code}`);
            }
            else {
                showError(t('errors.error'));
            }
        }
        catch (err) {
            showError(t('errors.error'));
        }
        finally {
            setLoading(false);
        }
    });
    useEffect(() => {
        if (!user)
            return;
        if (useMockApi) {
            // Если используется mock API, перенаправляем на страницу трекера инициативы
            navigate('/initiative-tracker', { replace: true });
            return;
        }
        setRoomsLoading(true);
        roomApi
            .list(user.id)
            .then((res) => setUserRooms(res.results || res))
            .catch(() => showError(t('errors.error')))
            .finally(() => setRoomsLoading(false));
    }, [user, useMockApi]);
    const handleCopyCode = (code, roomId) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield navigator.clipboard.writeText(code);
            showNotification(t('rooms.notifications.roomCodeCopied'));
        }
        catch (_a) {
            showError(t('errors.copyError'));
        }
    });
    const handleCopyLink = (code, roomId) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const url = `${window.location.origin}/room/${code}`;
            yield navigator.clipboard.writeText(url);
            showNotification(t('rooms.notifications.shareLinkCopied'));
        }
        catch (_a) {
            showError(t('errors.copyError'));
        }
    });
    const handleDeleteRoom = (roomId) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield roomApi.delete(roomId);
            setUserRooms((prev) => prev.filter((r) => r.id !== roomId));
            showNotification(t('rooms.notifications.roomDeleted'));
        }
        catch (_a) {
            showError(t('errors.error'));
        }
    });
    const handleEditRoom = (room) => {
        setEditingRoomId(room.id);
        setEditRoomName(room.name || '');
    };
    const handleEditRoomSave = (room) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            if (editRoomName.trim() !== room.name) {
                const updated = yield roomApi.update(room.id, { name: editRoomName });
                setUserRooms((prev) => prev.map((r) => (r.id === room.id ? Object.assign(Object.assign({}, r), { name: updated.name }) : r)));
            }
        }
        catch (_a) {
            showError(t('errors.error'));
        }
        finally {
            setEditingRoomId(null);
            setTooltip(Object.assign({}, DEFAULT_TOOLTIP));
        }
    });
    const handleEditRoomCancel = () => {
        setEditingRoomId(null);
        setEditRoomName('');
    };
    useEffect(() => {
        if (!editingRoomId)
            return;
        const handleClickOutside = (event) => {
            if (editInputRef.current &&
                !editInputRef.current.contains(event.target) &&
                !event.target.closest('.icon-btn')) {
                setEditingRoomId(null);
                setEditRoomName('');
                setTooltip(Object.assign({}, DEFAULT_TOOLTIP));
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [editingRoomId]);
    return (_jsx("div", { className: "rooms-page", children: _jsxs("div", { className: "wrapper", children: [_jsx(NotificationPopup, { notificationList: notificationList }), _jsx("h2", { children: user ? t('rooms.title') : t('rooms.joinRoom') }), _jsx("div", { className: "join-room-form", children: _jsxs("form", { onSubmit: handleJoin, children: [_jsxs("label", { children: [t('rooms.roomCode'), _jsx("input", { type: "text", value: roomCode, onChange: (e) => setRoomCode(e.target.value), 
                                        // placeholder={t('rooms.roomCode')}
                                        autoComplete: "off" })] }), _jsx("button", { type: "submit", disabled: loading, children: t('rooms.join') })] }) }), user && (_jsx(_Fragment, { children: _jsx("div", { className: "create-room-form", children: _jsxs("form", { onSubmit: handleCreate, className: "create-room-form", children: [_jsxs("label", { children: [t('rooms.roomName'), _jsx("input", { type: "text", value: createRoomName, onChange: (e) => setCreateRoomName(e.target.value), 
                                            // placeholder={t('rooms.roomName')}
                                            autoComplete: "off" })] }), _jsx("button", { type: "submit", disabled: loading, children: t('rooms.createRoom') })] }) }) })), user && (_jsx(_Fragment, { children: _jsxs("div", { className: "rooms-list-container", children: [_jsx("h3", { children: t('rooms.myRooms') }), roomsLoading ? (_jsx(Loader, {})) : userRooms.length === 0 ? (_jsx("div", { children: t('rooms.noRooms') })) : (_jsx("ul", { className: "user-rooms-list", children: userRooms.map((room) => (console.log(room),
                                    (_jsx("li", { className: "user-room-item", children: editingRoomId === room.id ? (_jsxs(_Fragment, { children: [_jsx("input", { ref: editInputRef, type: "text", value: editRoomName, onChange: (e) => setEditRoomName(e.target.value), style: { marginRight: 8, minWidth: 120 } }), _jsx("button", { className: "icon-btn", onClick: () => handleEditRoomSave(room), "aria-label": t('common.save'), children: _jsx(Icon, { name: "check" }) })] })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: "copy-buttons-container", children: [_jsxs("button", { type: "button", className: "icon-btn", onClick: () => handleCopyCode(room.code, room.id), onMouseEnter: () => setTooltip({ roomId: room.id, type: 'code' }), onMouseLeave: () => setTooltip({ roomId: null, type: null }), "aria-label": t('rooms.copyRoomCode'), children: [_jsx(Icon, { name: "copy_1" }), tooltip.roomId === room.id && tooltip.type === 'code' && (_jsx("span", { className: "tooltip", children: t('rooms.copyRoomCode') }))] }), _jsxs("button", { type: "button", className: "icon-btn", onClick: () => handleCopyLink(room.code, room.id), onMouseEnter: () => setTooltip({ roomId: room.id, type: 'link' }), onMouseLeave: () => setTooltip({ roomId: null, type: null }), "aria-label": t('rooms.shareRoom'), children: [_jsx(Icon, { name: "share" }), tooltip.roomId === room.id && tooltip.type === 'link' && (_jsx("span", { className: "tooltip", children: t('rooms.shareRoom') }))] })] }), _jsx("div", { className: "room-link", onClick: () => navigate(`/room/${room.code}`), children: _jsxs("div", { className: "room-name-code", children: [_jsx("span", { children: room.name || t('rooms.noName') }), _jsxs("span", { children: ["\u00A0(", room.code, ")"] })] }) }), _jsxs("div", { className: "edit-buttons-container", children: [_jsxs("button", { type: "button", className: "icon-btn", onClick: () => handleEditRoom(room), onMouseEnter: () => setTooltip({ roomId: room.id, type: 'edit' }), onMouseLeave: () => setTooltip({ roomId: null, type: null }), "aria-label": t('common.edit'), children: [_jsx(Icon, { name: "pencil" }), tooltip.roomId === room.id && tooltip.type === 'edit' && (_jsx("span", { className: "tooltip", children: t('common.edit') }))] }), _jsxs("button", { type: "button", className: "icon-btn", onClick: () => handleDeleteRoom(room.id), onMouseEnter: () => setTooltip({ roomId: room.id, type: 'delete' }), onMouseLeave: () => setTooltip({ roomId: null, type: null }), "aria-label": t('common.delete'), children: [_jsx(Icon, { name: "cross" }), tooltip.roomId === room.id && tooltip.type === 'delete' && (_jsx("span", { className: "tooltip", children: t('common.delete') }))] })] })] })) }, room.id)))) }))] }) }))] }) }));
};
export default RoomsPage;
